/**
 * FarloUiRevolverCarousel
 * A modern, physics-based carousel that is powerful, flexible, and accessible.
 * It's designed to be controlled via CSS for layout and data-attributes for behavior.
 *
 * @version 4.1.0 (Flawless Loop & CenterMode Engine)
 * @author Farlo
 */
class FarloUiRevolverCarousel {
	/**
	 * Initialises the carousel instance.
	 *
	 * @param {HTMLElement} container - The main carousel container element.
	 * @param {Object}      options   - Manual options to override defaults and data-attributes.
	 */
	constructor(container, options = {}) {
		this.container = container;
		if (!this.container || this.container.classList.contains('farlo-ui-revolver-carousel--loaded')) {
			return;
		}

		// --- CORE SETTINGS ---
		const defaultOptions = {
			arrows: false,
			autoplay: false,
			autoplaySpeed: 3000,
			pauseAutoplayOnHover: true,
			resumeAutoplayDelay: 5000,
			centerMode: true,
			friction: 0.92,
			freeScroll: false,
			groupCells: 1,
			loop: true,
			pagination: true,
			swipe: true,
			threshold: 10
		};

		this.settings = { ...defaultOptions, ...this.getDataAttributes(), ...options };
		this.originalContent = this.container.innerHTML;
		this.carouselID = `f-carousel-${ Math.random().toString(36).slice(2, 9) }`;
		this.isMotionReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		// --- STATE MANAGEMENT ---
		this.state = {
			currentSlide: 0, // [RE-ARCH] This is now the PHYSICAL index in the DOM
			isAnimating: false,
			pointerDown: false,
			startX: 0,
			deltaX: 0,
			x: 0 // The absolute X position of the track
		};
		this.isDragging = false;
		this.animationFrame = null;
		this.resizeTimeout = null;
		this.autoplayInterval = null;
		this.resumeAutoplayTimeout = null;

		this.init();
	}

	/**
	 * Reads data-attributes from the container to configure the carousel.
	 *
	 * @return {Object} - An object of settings derived from data-attributes.
	 */
	getDataAttributes() {
		const dataOptions = {};
		const MAPPING = {
			arrows: 'arrows', autoplay: 'autoplay', 'autoplaySpeed': 'autoplaySpeed',
			pauseAutoplayOnHover: 'pauseAutoplayOnHover', resumeAutoplayDelay: 'resumeAutoplayDelay',
			centerMode: 'centerMode', friction: 'friction', freeScroll: 'freeScroll',
			groupCells: 'groupCells', loop: 'loop', pagination: 'pagination',
			swipe: 'swipe', threshold: 'threshold'
		};
		for (const [attr, val] of Object.entries(this.container.dataset)) {
			const key = MAPPING[ attr ];
			if (key) {
				if (val === 'true') {
					dataOptions[ key ] = true;
				} else if (val === 'false') {
					dataOptions[ key ] = false;
				} else if (!isNaN(val)) {
					dataOptions[ key ] = parseFloat(val);
				} else {
					dataOptions[ key ] = val;
				}
			}
		}
		return dataOptions;
	}

	/**
	 * Main initialisation function. Orchestrates the setup process.
	 */
	init() {
		this.setupDOM();
		if (this.slideCount < 1) {
			return;
		}

		// Single slide → static, no clones, no controls, no listeners
		if (this.slideCount === 1) {
			this.container.classList.add('farlo-ui-revolver-carousel--static');
			return;
		}

		// Multi-slide setup
		this.container.classList.add('farlo-ui-revolver-carousel', 'farlo-ui-revolver-carousel--loaded');

		if (this.settings.loop) {
			this.cloneSlides();
			// refresh slides reference after cloning
			this.slides = Array.from(this.track.children);
		}
		this.createControls();

		// Wait for carousel to have actual dimensions before measuring
		this.waitForLayout();
	}

	/**
	 * Waits for the carousel to be laid out with actual dimensions before completing init.
	 */
	waitForLayout() {
		// Wait for images to load before measuring
		const images = Array.from(this.track.querySelectorAll('img'));

		if (images.length === 0) {
			// No images, can init immediately after layout
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					this.finishInit();
				});
			});
			return;
		}

		// Wait for all images to load or error
		const imagePromises = images.map((img) => {
			return new Promise((resolve) => {
				if (img.complete) {
					// Already loaded
					resolve();
				} else {
					img.addEventListener('load', resolve, { once: true });
					img.addEventListener('error', resolve, { once: true });
				}
			});
		});

		Promise.all(imagePromises).then(() => {
			// Images loaded, clear fallback and finish init
			if (this.fallbackTimeout) {
				clearTimeout(this.fallbackTimeout);
				this.fallbackTimeout = null;
			}
			// Now wait for layout
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					this.finishInit();
				});
			});
		});

		// Fallback: force init after 2 seconds even if images haven't loaded
		this.fallbackTimeout = setTimeout(() => {
			this.finishInit();
		}, 2000);
	}

	/**
	 * Completes initialization after DOM is laid out.
	 */
	finishInit() {
		// Prevent double initialization
		if (this.initialized) {
			return;
		}
		this.initialized = true;

		this.calculateMetrics();
		this.addEventListeners();

		// Start on the first "real" slide if looping, otherwise 0
		this.select(this.settings.loop ? this.slideCount : 0, false);

		this.dispatch('ready');
		if (this.settings.autoplay) {
			this.startAutoplay();
		}
	}

	/**
	 * Builds the necessary DOM structure (track, slides) and sets ARIA attributes.
	 */
	setupDOM() {
		this.container.setAttribute('data-carousel-id', this.carouselID);

		// collect only element children (ignore text nodes)
		// Work with existing DOM nodes to preserve any event listeners bound by other scripts
		this.slides = Array.from(this.container.children).filter((el) => el.nodeType === 1);
		this.slideCount = this.slides.length;

		if (this.slideCount === 0) {
			return;
		}

		const track = document.createElement('div');
		track.className = 'farlo-ui-revolver-carousel__cylinder';
		track.setAttribute('aria-live', 'polite');

		this.slides.forEach((slide, i) => {
			slide.classList.add('farlo-ui-revolver-carousel__chamber');
			slide.setAttribute('role', 'group');
			slide.setAttribute('aria-roledescription', 'slide');
			slide.setAttribute('aria-label', `${ i + 1 } of ${ this.slideCount }`);
			track.appendChild(slide);
		});

		this.container.innerHTML = '';
		this.container.appendChild(track);
		this.track = track;

		this.container.setAttribute('tabindex', '0');
		this.container.setAttribute('role', 'region');
		this.container.setAttribute('aria-label', 'carousel');
	}

	/**
	 * Measures critical dimensions and calculates layout properties.
	 */
	calculateMetrics() {
		this.containerWidth = this.container.offsetWidth;
		if (!this.slideCount) {
			return;
		}

		// Read horizontal gap from the track (flex gap)
		const ts = getComputedStyle(this.track);
		this.spacing = parseFloat(ts.columnGap || ts.gap) || 0;

		// Build arrays of physical measurements (supports variable widths)
		const kids = Array.from(this.track.children);
		this.physicalSlides = kids; // includes clones if present
		this.widths = kids.map((el) => el.offsetWidth);
		this.lefts = kids.map((el) => el.offsetLeft); // includes gaps
		this.centers = kids.map((_, i) => this.lefts[ i ] + this.widths[ i ] / 2);

		// Track size (last slide right edge)
		const lastIdx = kids.length - 1;
		const lastRight = this.lefts[ lastIdx ] + this.widths[ lastIdx ];
		this.trackWidth = lastRight;

		// For clamping when not looping
		this.maxScroll = Math.max(0, this.trackWidth - this.containerWidth);

		// Helper: where is the container visual center?
		this.containerCenter = this.containerWidth / 2;

		// Pick a canonical "real slide" width for center offset (first non-clone)
		const firstReal = this.slides[ 0 ];
		this.slideWidth = firstReal ? firstReal.offsetWidth : 0;

		// Check if measurements are valid (non-zero)
		if (this.containerWidth === 0 || this.slideWidth === 0) {
			console.warn('FarloUiRevolverCarousel: Zero width detected, measurements may be incorrect');
		}
	}

	/**
	 * Creates clones for seamless looping.
	 */
	cloneSlides() {
		const originalSlides = [...this.slides];
		const focusableSelector = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

		/**
		 * Prepend one full set of clones in reverse order so that the sequence reads
		 * correctly at the start of the track (clone of last slide appears first).
		 */
		for (let i = this.slideCount - 1; i >= 0; i--) {
			const clone = originalSlides[ i ].cloneNode(true);
			clone.classList.add('farlo-ui-revolver-carousel__clone');
			clone.setAttribute('aria-hidden', 'true');
			clone.querySelectorAll(focusableSelector).forEach((el) => {
				el.setAttribute('tabindex', '-1');
			});
			this.track.insertBefore(clone, this.track.firstChild);
		}

		/**
		 * Append one full set of clones in forward order to provide a seamless
		 * loop buffer at the end of the track.
		 */
		for (let i = 0; i < this.slideCount; i ++) {
			const clone = originalSlides[ i ].cloneNode(true);
			clone.classList.add('farlo-ui-revolver-carousel__clone');
			clone.setAttribute('aria-hidden', 'true');
			clone.querySelectorAll(focusableSelector).forEach((el) => {
				el.setAttribute('tabindex', '-1');
			});
			this.track.appendChild(clone);
		}

		this.slides = Array.from(this.track.children);
	}

	/**
	 * Attaches all necessary event listeners.
	 */
	addEventListeners() {
		if (this.settings.arrows) {
			this.prevArrow.addEventListener('click', () => this.previous());
			this.nextArrow.addEventListener('click', (e) => {
				e.preventDefault();
				e.stopPropagation();
				this.next();
			});
		}
		if (this.settings.swipe) {
			this.track.addEventListener('pointerdown', this.onPointerDown.bind(this));
			window.addEventListener('pointermove', this.onPointerMove.bind(this));
			window.addEventListener('pointerup', this.onPointerUp.bind(this));
			this.track.addEventListener('click', this.onTrackClick.bind(this), true);
			this.track.addEventListener('dragstart', (e) => e.preventDefault());
		}
		if (this.settings.autoplay && this.settings.pauseAutoplayOnHover) {
			this.container.addEventListener('mouseenter', () => this.pauseAutoplay());
			this.container.addEventListener('mouseleave', () => this.resumeAutoplay());
		}
		this.track.addEventListener('transitionend', this.onTransitionEnd.bind(this));
		window.addEventListener('resize', this.debouncedResize.bind(this));
		this.container.addEventListener('keydown', this.onKeyDown.bind(this));
	}

	onKeyDown(e) {
		this.pauseAutoplay();
		switch (e.key) {
			case 'ArrowLeft': e.preventDefault(); this.previous(); break;
			case 'ArrowRight': e.preventDefault(); this.next(); break;
			case 'Home': e.preventDefault(); this.select(this.settings.loop ? this.slideCount : 0); break;
			case 'End': e.preventDefault(); this.select(this.settings.loop ? this.slideCount * 2 - 1 : this.slideCount - 1); break;
		}
		this.resumeAutoplay();
	}

	onPointerDown(e) {
		if (this.state.isAnimating || !e.isPrimary) {
			return;
		}
		this.pauseAutoplay();
		this.state.pointerDown = true;
		this.startX = e.clientX;
		this.startTime = Date.now();
		this.state.deltaX = 0;
		this.track.style.transition = 'none';
		this.isDragging = false;
		this.dispatch('dragStart');
	}

	onPointerMove(e) {
		if (!this.state.pointerDown || !e.isPrimary) {
			return;
		}
		this.state.deltaX = e.clientX - this.startX;
		if (Math.abs(this.state.deltaX) > this.settings.threshold && !this.isDragging) {
			this.isDragging = true;
			this.container.classList.add('is-dragging');
		}
		if (this.isDragging) {
			this.track.style.transform = `translateX(${ this.state.x + this.state.deltaX }px)`;
		}
	}

	onPointerUp(e) {
		if (!this.state.pointerDown || !e.isPrimary) {
			return;
		}
		this.state.pointerDown = false;
		this.container.classList.remove('is-dragging');

		if (this.isDragging) {
			this.state.x += this.state.deltaX;

			// Calculate swipe velocity (px/ms)
			const elapsed = Date.now() - this.startTime;
			const velocity = this.state.deltaX / Math.max(elapsed, 1);

			// Fast swipe: advance one slide in the swipe direction
			if (Math.abs(velocity) > 0.3) {
				const direction = velocity > 0 ? -1 : 1;
				this.select(this.state.currentSlide + direction * this.settings.groupCells);
			} else {
				// Slow drag: snap to nearest slide center
				const currentCenterInTrack = -this.state.x + this.containerCenter;
				let bestIdx = 0;
				let bestDist = Infinity;
				for (let i = 0; i < this.centers.length; i ++) {
					const d = Math.abs(this.centers[ i ] - currentCenterInTrack);
					if (d < bestDist) {
						bestDist = d; bestIdx = i;
					}
				}
				this.select(bestIdx);
			}
		}

		this.resumeAutoplay();
		this.dispatch('dragEnd');
	}

	onTrackClick(e) {
		if (this.isDragging) {
			e.preventDefault();
			e.stopPropagation();
		}
	}

	select(index, smooth = true) {
		this.state.currentSlide = index; // The index is now always the PHYSICAL index
		this.update(smooth);
	}

	next() {
		this.select(this.state.currentSlide + this.settings.groupCells);
	}
	previous() {
		this.select(this.state.currentSlide - this.settings.groupCells);
	}

	getTargetXForIndex(physicalIndex) {
		// Align the chosen slide's center to the container center when centerMode,
		// otherwise align its left edge to container left.
		const slideCenter = this.centers[ physicalIndex ] || 0;
		const slideLeft = this.lefts[ physicalIndex ] || 0;

		if (this.settings.centerMode) {
			return -(slideCenter - this.containerCenter);
		}
		return -slideLeft;
	}

	/**
	 * Applies the transform to the track to position the slides. This is the core engine.
	 *
	 * @param {boolean} smooth - Whether to animate the transition.
	 */
	update(smooth = true) {
		if (this.isMotionReduced) {
			smooth = false;
		}
		this.state.isAnimating = true;

		let targetX = this.getTargetXForIndex(this.state.currentSlide);

		if (!this.settings.loop) {
			// Clamp to bounds when not looping
			targetX = Math.min(0, Math.max(-this.maxScroll, targetX));
		}

		this.track.style.transition = smooth ? 'transform 0.6s cubic-bezier(0.25,0.46,0.45,0.94)' : 'none';
		this.track.style.transform = `translateX(${ targetX }px)`;
		this.state.x = targetX;

		if (!smooth) {
			this.onTransitionEnd();
		}

		this.updateControls();
		this.dispatch('select', { index: this.getRealIndex() });
	}

	/**
	 * Handles the seamless loop repositioning after a transition ends.
	 */
	onTransitionEnd() {
		this.state.isAnimating = false;
		if (this.settings.loop) {
			const isAtStartClone = this.state.currentSlide < this.slideCount;
			const isAtEndClone = this.state.currentSlide >= this.slideCount * 2;

			if (isAtStartClone || isAtEndClone) {
				const logicalIndex = this.getRealIndex();
				this.select(logicalIndex + this.slideCount, false); // Silently jump
			}
		}
	}

	/**
	 * Creates pagination and arrow controls.
	 */
	createControls() {
		const oldControls = this.container.querySelectorAll('.farlo-ui-revolver-carousel__arrow, .farlo-ui-revolver-carousel__pagination');
		oldControls.forEach((control) => control.remove());
		if (this.settings.arrows) {
			this.prevArrow = document.createElement('button');
			this.prevArrow.className = 'farlo-ui-revolver-carousel__arrow farlo-ui-revolver-carousel__arrow--prev';
			this.prevArrow.innerHTML = '←';
			this.prevArrow.setAttribute('aria-label', 'Previous');
			this.nextArrow = document.createElement('button');
			this.nextArrow.className = 'farlo-ui-revolver-carousel__arrow farlo-ui-revolver-carousel__arrow--next';
			this.nextArrow.innerHTML = '→';
			this.nextArrow.setAttribute('aria-label', 'Next');
			this.container.appendChild(this.prevArrow);
			this.container.appendChild(this.nextArrow);
		}
		if (this.settings.pagination && this.slideCount > 1) {
			this.pagination = document.createElement('div');
			this.pagination.className = 'farlo-ui-revolver-carousel__pagination';
			for (let i = 0; i < this.slideCount; i ++) {
				const dot = document.createElement('button');
				dot.className = 'farlo-ui-revolver-carousel__pagination-sightdot';
				dot.setAttribute('aria-label', `Go to slide ${ i + 1 }`);
				dot.addEventListener('click', () => {
					if (this.state.isAnimating) {
						return;
					}
					this.pauseAutoplay();
					this.select(i + (this.settings.loop ? this.slideCount : 0));
					this.resumeAutoplay();
				});
				this.pagination.appendChild(dot);
			}
			this.container.appendChild(this.pagination);
		}
	}

	/**
	 * Updates the active state of controls (dots, arrows).
	 */
	updateControls() {
		const logicalIndex = this.getRealIndex();
		if (this.settings.pagination && this.pagination) {
			this.pagination.querySelectorAll('.farlo-ui-revolver-carousel__pagination-sightdot').forEach((dot, i) => {
				dot.classList.toggle('farlo-ui-revolver-carousel__pagination-sightdot--active', i === logicalIndex);
				dot.setAttribute('aria-current', i === logicalIndex ? 'true' : 'false');
			});
		}
		if (this.settings.arrows && !this.settings.loop) {
			const logicalMaxSlide = Math.floor(this.maxScroll / this.slideWidthWithGap);
			this.prevArrow.disabled = this.state.currentSlide <= 0;
			this.nextArrow.disabled = this.state.currentSlide >= logicalMaxSlide;
		}
	}

	/**
	 * Gets the real slide index (0 to slideCount - 1), ignoring clones.
	 *
	 * @return {number}
	 */
	getRealIndex() {
		if (this.slideCount === 0) {
			return 0;
		}
		const physicalIndex = this.state.currentSlide;
		return (physicalIndex % this.slideCount + this.slideCount) % this.slideCount;
	}

	/**
	 * Dispatches a custom event from the container.
	 *
	 * @param eventName
	 * @param detail
	 */
	dispatch(eventName, detail = {}) {
		this.container.dispatchEvent(new CustomEvent(eventName, { detail, bubbles: true }));
	}

	/**
	 * Debounced resize handler to recalculate metrics and reposition.
	 */
	debouncedResize() {
		clearTimeout(this.resizeTimeout);
		this.resizeTimeout = setTimeout(() => this.handleResize(), 300);
	}

	/**
	 * Handles window resize by recalculating metrics and updating position.
	 */
	handleResize() {
		if (!this.track || this.slideCount < 2) {
			return;
		}

		// Recalculate all dimensions
		this.calculateMetrics();

		// Update the current position based on new dimensions
		this.update(false);
	}

	/**
	 * Starts/resumes the autoplay interval.
	 */
	startAutoplay() {
		this.pauseAutoplay();
		if (!this.settings.autoplay) {
			return;
		}
		this.autoplayInterval = setInterval(() => {
			const logicalMaxSlide = Math.floor(this.maxScroll / this.slideWidthWithGap);
			if (!this.settings.loop && this.getRealIndex() >= logicalMaxSlide) {
				this.pauseAutoplay();
				return;
			}
			this.next();
		}, this.settings.autoplaySpeed);
	}

	/**
	 * Pauses the autoplay.
	 */
	pauseAutoplay() {
		clearInterval(this.autoplayInterval);
		clearTimeout(this.resumeAutoplayTimeout);
	}

	/**
	 * Resumes autoplay after a delay.
	 */
	resumeAutoplay() {
		this.pauseAutoplay();
		if (!this.settings.autoplay) {
			return;
		}
		this.resumeAutoplayTimeout = setTimeout(() => {
			this.startAutoplay();
		}, this.settings.resumeAutoplayDelay);
	}

	/**
	 * Destroys the carousel instance.
	 */
	destroy() {
		this.pauseAutoplay();
		window.removeEventListener('resize', this.debouncedResize);
		this.container.innerHTML = this.originalContent;
		this.container.classList.remove('farlo-ui-revolver-carousel', 'farlo-ui-revolver-carousel--loaded');
		this.container.removeAttribute('data-carousel-id');
		this.dispatch('destroy');
	}
}

export default FarloUiRevolverCarousel;
